/**
 * useAuthGate — hook for auth-gated actions on public pages.
 *
 * Returns helpers that let public pages show interactive UI (like, reply, post)
 * but redirect to /login when an anonymous user tries to act.
 * After login, the user is redirected back to the original page.
 */

import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';

export interface AuthGate {
  /** Current Firebase user (null if anonymous) */
  user: ReturnType<typeof useAuth>['user'];
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /**
   * Wrap an action so it only executes when authenticated.
   * If anonymous, redirects to /login with a redirect param back to the current page.
   */
  requireAuth: <T extends unknown[]>(action: (...args: T) => void) => (...args: T) => void;
  /** Navigate to login with redirect back to current page */
  redirectToLogin: () => void;
}

export function useAuthGate(): AuthGate {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = !!user;

  const redirectToLogin = useCallback(() => {
    const currentPath = location.pathname + location.search;
    navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
  }, [navigate, location]);

  const requireAuth = useCallback(
    <T extends unknown[]>(action: (...args: T) => void) => {
      return (...args: T) => {
        if (!user) {
          redirectToLogin();
          return;
        }
        action(...args);
      };
    },
    [user, redirectToLogin]
  );

  return { user, isAuthenticated, requireAuth, redirectToLogin };
}

export default useAuthGate;
